// The event surfaces (bead hf-e4-events-needs-6r5.1).
//
// PURE COMPONENTS over the view models in `eventData.ts`. They take data and
// callbacks and know nothing about where either comes from — the same arrangement the
// plan surfaces use, and the reason those survived the swap from fixtures to live
// Convex queries without being rewritten.
//
// COPY COMES FROM `@herefor/product-language`, ALWAYS. Nothing here composes a
// user-facing sentence; the copy guard enforces it across every TSX file under
// apps/web, and the tone rules are somebody else's considered work.
//
// COMPONENTS COME FROM `@herefor/design-system`. No bare inputs, no hand-rolled
// dialogs: the kernel components carry the focus, labelling and target-size
// behaviour that WCAG 2.2 AA needs, and a one-off `<input>` here would quietly opt
// out of all of it.
//
// ── THE AFFORDANCE LAYER IS THE POINT ───────────────────────────────────────────
//
// Denial is already proved at the function layer — the negative-authz suite covers
// every capability. What THIS bead tests is that a person who cannot do something is
// never shown a control for it. The two are different failures: the first is a
// security hole, the second is offering somebody a button that will reject them,
// which is its own small cruelty. Every action below is gated on an explicit
// capability flag the SERVER decided; nothing here infers permission from a role.

import {
  Button,
  Card,
  Dialog,
  EmptyState,
  StateChip,
  StatusMessage,
  TextInput,
} from "@herefor/design-system";
import {
  EVENT_SURFACE_MESSAGES as COPY,
  LIFECYCLE_SURFACE_MESSAGES,
  STATUS_CONTEXT,
} from "@herefor/product-language";
import type { ChangeClassification } from "@herefor/domain-kernel";
import { useId, useState, type ChangeEvent } from "react";

import type {
  EventDetailView,
  EventFormValues,
  EventHistoryEntryView,
  FieldProblem,
} from "./eventData";

/**
 * What the server says this person may do here.
 *
 * Flags, not a role. The client renders what the server says it may (AUT-007), and a
 * component that decided "organisers can cancel" would be a second, drifting copy of
 * the capability model living in a browser bundle.
 */
export interface EventAffordances {
  readonly canEdit: boolean;
  readonly canCancel: boolean;
  /** Deleting is only ever legal for a draft (EVT-013); the surface checks both. */
  readonly canDeleteDraft: boolean;
}

export const VIEW_ONLY_AFFORDANCES: EventAffordances = {
  canEdit: false,
  canCancel: false,
  canDeleteDraft: false,
};

/* ================================================================== the form === */

export interface EventFormProps {
  readonly values: EventFormValues;
  readonly onChange: (values: EventFormValues) => void;
  readonly onSubmit: () => void;
  readonly problems: readonly FieldProblem[];
  readonly submitLabel: string;
  readonly heading: string;
  /** The form is also the page heading on the live create/edit routes. */
  readonly headingLevel?: 1 | 2;
  /**
   * The kernel's verdict on the pending edit, or null on a create.
   *
   * Shown BEFORE saving (§10.8: no hidden invalidation). An organiser who is about to
   * make somebody recheck their commitment should learn that while they can still
   * change their mind, not in a confirmation afterwards.
   */
  readonly pendingChange?: ChangeClassification | null;
  readonly busy?: boolean;
}

export function EventForm({
  values,
  onChange,
  onSubmit,
  problems,
  submitLabel,
  heading,
  headingLevel = 2,
  pendingChange = null,
  busy = false,
}: EventFormProps) {
  const headingId = useId();
  const Heading = headingLevel === 1 ? "h1" : "h2";
  // The design system's TextInput spreads native input attributes, so the handler is
  // the ordinary `onChange` rather than a bespoke one. Keeping the native contract is
  // the point of the kernel component: nothing here re-implements an input.
  const set = (key: keyof EventFormValues) => (changeEvent: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...values, [key]: changeEvent.target.value });
  };

  const problemFor = (field: string): string | undefined =>
    problems.find((problem) => problem.field === field)?.message;

  const material = pendingChange?.outcome === "material";

  return (
    <form
      className="event-form"
      aria-labelledby={headingId}
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();
        onSubmit();
      }}
    >
      <Heading id={headingId}>{heading}</Heading>

      {material ? (
        // Not an error — nothing is wrong. It is a consequence the organiser is
        // entitled to know about before they commit to it.
        <StatusMessage tone="warning">
          <strong>{COPY.materialWarningTitle}</strong> {COPY.materialWarningBody}
        </StatusMessage>
      ) : null}

      <TextInput
        label={COPY.titleLabel}
        hint={COPY.titleHint}
        value={values.title}
        onChange={set("title")}
        error={problemFor("title")}
        required
      />
      <TextInput
        label={COPY.shareTitleLabel}
        hint={COPY.shareTitleHint}
        value={values.shareTitle}
        onChange={set("shareTitle")}
        error={problemFor("shareTitle")}
        required
      />
      <TextInput
        label={COPY.categoryLabel}
        value={values.category}
        onChange={set("category")}
        error={problemFor("category")}
        required
      />
      <TextInput
        label={COPY.startsAtLabel}
        hint={COPY.timeUnknownHint}
        type="datetime-local"
        value={values.startsAtLocal}
        onChange={set("startsAtLocal")}
        error={problemFor("startsAt")}
      />
      <TextInput
        label={COPY.endsAtLabel}
        type="datetime-local"
        value={values.endsAtLocal}
        onChange={set("endsAtLocal")}
        error={problemFor("endsAt")}
      />
      <TextInput
        label={COPY.locationLabel}
        value={values.locationLabel}
        onChange={set("locationLabel")}
        error={problemFor("locationLabel")}
      />
      <TextInput
        label={COPY.locationPrivateLabel}
        hint={COPY.locationPrivateHint}
        value={values.locationPrivate}
        onChange={set("locationPrivate")}
        error={problemFor("locationPrivate")}
      />
      <TextInput
        label={COPY.practicalNoteLabel}
        hint={COPY.practicalNoteHint}
        value={values.practicalNote}
        onChange={set("practicalNote")}
        error={problemFor("practicalNote")}
      />

      {problemFor("form") === undefined ? null : (
        // `assertive`, because this one interrupts: the person pressed save and it
        // did not happen. Everything else in this product announces politely.
        <StatusMessage tone="error" live="assertive">
          {problemFor("form")}
        </StatusMessage>
      )}

      <Button type="submit" variant="primary" disabled={busy}>
        {submitLabel}
      </Button>
    </form>
  );
}

/* ================================================================ the detail === */

export interface EventDetailProps {
  readonly event: EventDetailView;
  readonly affordances: EventAffordances;
  /** How many people have agreed to help with needs linked to this event. */
  readonly committedPeopleCount: number;
  /** How many linked needs this event cancellation will close. */
  readonly linkedNeedCount: number;
  readonly onEdit: () => void;
  readonly onPublish: () => void;
  readonly onMarkDone: () => void;
  readonly onCancel: () => void;
  readonly onDeleteDraft: () => void;
  readonly actionProblem?: string | null;
  readonly busy?: boolean;
  /** The event title is the page heading on the live detail route. */
  readonly headingLevel?: 1 | 2;
}

/**
 * One event, with the actions this person may actually take.
 *
 * The publish, cancel and delete paths all go through {@link Dialog}: each is either
 * irreversible or visible to the whole family, and none of them should be one
 * mis-tap away.
 */
export function EventDetail({
  event,
  affordances,
  committedPeopleCount,
  linkedNeedCount,
  onEdit,
  onPublish,
  onMarkDone,
  onCancel,
  onDeleteDraft,
  actionProblem = null,
  busy = false,
  headingLevel = 2,
}: EventDetailProps) {
  const [confirming, setConfirming] = useState<"publish" | "done" | "cancel" | "delete" | null>(
    null,
  );
  const isDraft = event.state === "draft";
  const isTerminal = event.state === "cancelled" || event.state === "completed";

  // The caller states whether this card is the page or an embedded detail. The
  // design-system card owns the actual heading element in either case.
  return (
    <Card title={event.title} titleLevel={headingLevel}>
      <p className="event-detail__state">
        <StateChip label={COPY.states[event.state]} state={chipStateFor(event.state)} />
        {event.isPast ? (
          // Plan §10.2: Past is a display treatment ALONGSIDE the state, never
          // instead of it. A scheduled event whose time has gone is both.
          <StateChip label={STATUS_CONTEXT.past} state="past" />
        ) : null}
      </p>

      {isDraft ? <StatusMessage tone="info">{COPY.draftExplanation}</StatusMessage> : null}

      <dl className="event-detail__facts">
        <Fact label={COPY.startsAtLabel} value={event.windowLabel} />
        <Fact label={COPY.locationLabel} value={event.locationLabel} />
        <Fact label={COPY.locationPrivateLabel} value={event.locationPrivate} />
        <Fact label={COPY.practicalNoteLabel} value={event.practicalNote} />
        <Fact label={COPY.shareTitleLabel} value={event.shareTitle} />
      </dl>

      <p className="event-detail__note">{COPY.eventNeverCovered}</p>

      {actionProblem === null ? null : (
        <StatusMessage tone="error" live="assertive">
          {actionProblem}
        </StatusMessage>
      )}

      <div className="event-detail__actions">
        {affordances.canEdit && !isTerminal ? (
          <Button variant="secondary" onClick={onEdit} disabled={busy}>
            {COPY.edit}
          </Button>
        ) : null}

        {affordances.canEdit && isDraft ? (
          <Button
            variant="primary"
            onClick={() => {
              setConfirming("publish");
            }}
            disabled={busy}
          >
            {COPY.publish}
          </Button>
        ) : null}

        {affordances.canCancel && !isDraft && !isTerminal ? (
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming("cancel");
            }}
            disabled={busy}
          >
            {COPY.cancelEvent}
          </Button>
        ) : null}

        {affordances.canEdit && !isDraft && !isTerminal && event.isPast ? (
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming("done");
            }}
            disabled={busy}
          >
            {LIFECYCLE_SURFACE_MESSAGES.event.markDone}
          </Button>
        ) : null}

        {/* EVT-013: deleting is offered ONLY for a draft. A published event is
            cancelled, because a cancelled hospital run is a fact the family may need
            to look back on and deleting it quietly rewrites what happened. */}
        {affordances.canDeleteDraft && isDraft ? (
          <Button
            variant="danger"
            onClick={() => {
              setConfirming("delete");
            }}
            disabled={busy}
          >
            {COPY.deleteDraft}
          </Button>
        ) : null}
      </div>

      <Dialog
        open={confirming === "publish"}
        title={COPY.publishTitle}
        onClose={() => {
          setConfirming(null);
        }}
        footer={
          <>
            <Button
              variant="primary"
              onClick={() => {
                setConfirming(null);
                onPublish();
              }}
            >
              {COPY.publish}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {COPY.keep}
            </Button>
          </>
        }
      >
        <p>{COPY.publishBody}</p>
      </Dialog>

      <Dialog
        open={confirming === "done"}
        title={LIFECYCLE_SURFACE_MESSAGES.event.markDoneTitle}
        onClose={() => {
          setConfirming(null);
        }}
        footer={
          <>
            <Button
              variant="primary"
              onClick={() => {
                setConfirming(null);
                onMarkDone();
              }}
            >
              {LIFECYCLE_SURFACE_MESSAGES.event.markDoneConfirm}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {COPY.keep}
            </Button>
          </>
        }
      >
        <p>{LIFECYCLE_SURFACE_MESSAGES.event.markDoneBody}</p>
      </Dialog>

      <Dialog
        open={confirming === "cancel"}
        title={COPY.cancelTitle}
        onClose={() => {
          setConfirming(null);
        }}
        footer={
          <>
            <Button
              variant="danger"
              onClick={() => {
                setConfirming(null);
                onCancel();
              }}
            >
              {COPY.cancelConfirm}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {COPY.keep}
            </Button>
          </>
        }
      >
        <p>{COPY.cancelBody}</p>
        {linkedNeedCount > 0 ? (
          <p>{LIFECYCLE_SURFACE_MESSAGES.event.cancelNeedImpact(linkedNeedCount)}</p>
        ) : null}
        {/* The blast radius, as a count of people — the fact most likely to change
            an organiser's mind, and the reason this is a dialog at all. */}
        {committedPeopleCount > 0 ? <p>{COPY.cancelImpact(committedPeopleCount)}</p> : null}
      </Dialog>

      <Dialog
        open={confirming === "delete"}
        title={COPY.deleteDraftTitle}
        onClose={() => {
          setConfirming(null);
        }}
        footer={
          <>
            <Button
              variant="danger"
              onClick={() => {
                setConfirming(null);
                onDeleteDraft();
              }}
            >
              {COPY.deleteDraftConfirm}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {COPY.keep}
            </Button>
          </>
        }
      >
        <p>{COPY.deleteDraftBody}</p>
      </Dialog>
    </Card>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string | null }) {
  if (value === null || value === "") return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/* =============================================================== the history === */

export interface EventHistoryProps {
  readonly entries: readonly EventHistoryEntryView[];
  readonly headingLevel?: 2 | 3;
}

/**
 * The append-only material history (EVT-014, plan §28.6).
 *
 * It says THAT the location changed, never what it changed to: history carries a
 * fingerprint rather than the address, so correcting or erasing a location does not
 * leave copies behind in a table that outlives every edit.
 */
export function EventHistory({ entries, headingLevel = 3 }: EventHistoryProps) {
  if (entries.length === 0) {
    return <EmptyState title={COPY.historyTitle} description={COPY.historyEmpty} />;
  }

  return (
    <section className="event-history">
      {headingLevel === 2 ? <h2>{COPY.historyTitle}</h2> : <h3>{COPY.historyTitle}</h3>}
      <p>{COPY.historyIntroduction}</p>
      <ol className="event-history__list">
        {entries.map((entry) => (
          <li key={entry.revision} className="event-history__entry">
            <p className="event-history__revision">{COPY.historyEntry(entry.revision)}</p>
            <p className="event-history__when">{entry.changedAtLabel}</p>
            <ul className="event-history__changes">
              {entry.changes.map((change) => (
                <li key={change}>{CHANGE_COPY[change]}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}

const CHANGE_COPY = {
  time: COPY.historyTimeChanged,
  location: COPY.historyLocationChanged,
  created: COPY.historyCreated,
} as const;

/**
 * The record's state → a chip token.
 *
 * Exhaustive, so a new state cannot fall through to a default colour. These are the
 * EVENT's own four states (plan §28.3), never the five user-visible coverage
 * statuses: an event is never itself covered (EVT-018).
 */
function chipStateFor(state: EventDetailView["state"]) {
  switch (state) {
    case "draft":
      return "needsSomeone" as const;
    case "scheduled":
      return "covered" as const;
    case "completed":
      return "done" as const;
    case "cancelled":
      return "cancelled" as const;
  }
}

export { COPY as EVENT_COPY };
